import React, { useState } from 'react';
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Calculate, AcUnit } from '@mui/icons-material';
import {
  CalculateTonnage,
  TonnagePayload,
  TonnageResult,
} from '../../services/utility';
import useSnackbar from '../../hooks/useSnackbar';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': { borderColor: '#565656' },
    '&:hover fieldset': { borderColor: '#9e9e9e' },
    '&.Mui-focused fieldset': { borderColor: '#E31E25' },
  },
  '& .MuiInputLabel-root': { color: '#9e9e9e' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#E31E25' },
};

const TonnageCalculator: React.FC = () => {
  const [areaMode, setAreaMode] = useState<'DIMENSIONS' | 'AREA'>(
    'DIMENSIONS',
  );
  const [roomLengthFt, setRoomLengthFt] = useState('');
  const [roomWidthFt, setRoomWidthFt] = useState('');
  const [areaSqFt, setAreaSqFt] = useState('');
  const [ceilingHeightFt, setCeilingHeightFt] = useState('10');
  const [occupants, setOccupants] = useState('2');
  const [windows, setWindows] = useState('1');
  const [sunExposure, setSunExposure] = useState<'LOW' | 'MEDIUM' | 'HIGH'>(
    'MEDIUM',
  );
  const [isTopFloor, setIsTopFloor] = useState(false);
  const [hasKitchen, setHasKitchen] = useState(false);

  const [result, setResult] = useState<TonnageResult | null>(null);
  const [loading, setLoading] = useState(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const validate = (): string | null => {
    if (areaMode === 'AREA') {
      const area = Number(areaSqFt);
      if (!area || area <= 0) return 'Enter a valid area in sq ft';
      if (area < 10 || area > 10000)
        return 'Area must be between 10 and 10,000 sq ft';
    } else {
      const length = Number(roomLengthFt);
      const width = Number(roomWidthFt);
      if (!length || length <= 0 || !width || width <= 0)
        return 'Enter valid room length and width in ft';
      const area = length * width;
      if (area < 10 || area > 10000)
        return 'Room area must be between 10 and 10,000 sq ft';
    }
    return null;
  };

  const handleCalculate = async () => {
    const validationError = validate();
    if (validationError) {
      showSnackbar(validationError, 'error');
      return;
    }

    const payload: TonnagePayload = {
      ceilingHeightFt: Number(ceilingHeightFt) || undefined,
      occupants: Number(occupants) || undefined,
      windows: Number(windows) || undefined,
      sunExposure,
      isTopFloor,
      hasKitchen,
    };

    if (areaMode === 'AREA') {
      payload.areaSqFt = Number(areaSqFt);
    } else {
      payload.roomLengthFt = Number(roomLengthFt);
      payload.roomWidthFt = Number(roomWidthFt);
    }

    try {
      setLoading(true);
      const data = await CalculateTonnage(payload);
      setResult(data);
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to calculate tonnage',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ m: 3 }}>
      <Box
        sx={{
          backgroundColor: '#1C1C1C',
          color: '#fff',
          borderRadius: 2,
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AcUnit sx={{ color: '#E31E25' }} />
              <Typography variant="h6">Tonnage Calculator</Typography>
            </Box>
          }
        />
        <Divider sx={{ borderColor: '#565656' }} />

        <CardContent sx={{ backgroundColor: '#232323', p: 3 }}>
          <Grid container spacing={3}>
            {/* Input form */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Room Details
              </Typography>

              <ToggleButtonGroup
                value={areaMode}
                exclusive
                size="small"
                onChange={(_, value) => value && setAreaMode(value)}
                sx={{
                  mb: 2,
                  '& .MuiToggleButton-root': {
                    color: '#9e9e9e',
                    borderColor: '#565656',
                    '&.Mui-selected': {
                      color: '#fff',
                      backgroundColor: '#E31E25',
                      '&:hover': { backgroundColor: '#C62828' },
                    },
                  },
                }}
              >
                <ToggleButton value="DIMENSIONS">Length x Width</ToggleButton>
                <ToggleButton value="AREA">Direct Area</ToggleButton>
              </ToggleButtonGroup>

              <Grid container spacing={2}>
                {areaMode === 'DIMENSIONS' ? (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Room Length (ft)"
                        type="number"
                        value={roomLengthFt}
                        onChange={(e) => setRoomLengthFt(e.target.value)}
                        sx={inputSx}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Room Width (ft)"
                        type="number"
                        value={roomWidthFt}
                        onChange={(e) => setRoomWidthFt(e.target.value)}
                        sx={inputSx}
                      />
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Area (sq ft)"
                      type="number"
                      value={areaSqFt}
                      onChange={(e) => setAreaSqFt(e.target.value)}
                      sx={inputSx}
                    />
                  </Grid>
                )}

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Ceiling Height (ft)"
                    type="number"
                    value={ceilingHeightFt}
                    onChange={(e) => setCeilingHeightFt(e.target.value)}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Occupants"
                    type="number"
                    value={occupants}
                    onChange={(e) => setOccupants(e.target.value)}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Windows"
                    type="number"
                    value={windows}
                    onChange={(e) => setWindows(e.target.value)}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth sx={inputSx}>
                    <InputLabel>Sun Exposure</InputLabel>
                    <Select
                      value={sunExposure}
                      label="Sun Exposure"
                      onChange={(e) =>
                        setSunExposure(
                          e.target.value as 'LOW' | 'MEDIUM' | 'HIGH',
                        )
                      }
                      sx={{
                        color: '#fff',
                        '& .MuiSvgIcon-root': { color: '#9e9e9e' },
                      }}
                    >
                      <MenuItem value="LOW">Low</MenuItem>
                      <MenuItem value="MEDIUM">Medium</MenuItem>
                      <MenuItem value="HIGH">High</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isTopFloor}
                        onChange={(e) => setIsTopFloor(e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E31E25',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                            { backgroundColor: '#E31E25' },
                        }}
                      />
                    }
                    label="Top Floor"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={hasKitchen}
                        onChange={(e) => setHasKitchen(e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E31E25',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                            { backgroundColor: '#E31E25' },
                        }}
                      />
                    }
                    label="Kitchen / Heat Appliances"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    startIcon={
                      loading ? (
                        <CircularProgress size={18} sx={{ color: '#fff' }} />
                      ) : (
                        <Calculate />
                      )
                    }
                    disabled={loading}
                    onClick={handleCalculate}
                    sx={{
                      color: '#fff',
                      backgroundColor: '#E31E25',
                      '&:hover': { backgroundColor: '#C62828' },
                      padding: '12px 20px',
                      borderRadius: '14px',
                    }}
                  >
                    {loading ? 'Calculating…' : 'Calculate Tonnage'}
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* Result */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Recommendation
              </Typography>

              {!result ? (
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: '#2E2E2E',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                  }}
                >
                  <AcUnit sx={{ color: '#565656', fontSize: 48, mb: 1 }} />
                  <Typography sx={{ color: '#9e9e9e' }}>
                    Enter the room details and press Calculate to get the
                    recommended AC tonnage.
                  </Typography>
                </Paper>
              ) : (
                <>
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: '#2E2E2E',
                      borderRadius: 2,
                      p: 3,
                      mb: 2,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#9e9e9e' }}>
                      Recommended
                    </Typography>
                    <Typography
                      sx={{
                        color: '#0BE600',
                        fontWeight: 700,
                        fontSize: 36,
                        lineHeight: 1.2,
                      }}
                    >
                      {result.recommendation}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9e9e9e' }}>
                      {result.exactTons} tons exact ·{' '}
                      {result.totalBtu.toLocaleString()} BTU ·{' '}
                      {result.areaSqFt.toLocaleString()} sq ft
                    </Typography>
                  </Paper>

                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                  >
                    {result.breakdown.map((item) => (
                      <Paper
                        key={item.label}
                        elevation={0}
                        sx={{
                          backgroundColor: '#2E2E2E',
                          borderRadius: 1,
                          px: 2,
                          py: 1.25,
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: '#9e9e9e' }}
                        >
                          {item.label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#fff' }}>
                          {item.btu >= 0 ? '+' : ''}
                          {item.btu.toLocaleString()} BTU
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
        </CardContent>
        <Divider sx={{ borderColor: '#565656' }} />
      </Box>

      {SnackbarComponent}
    </Box>
  );
};

export default TonnageCalculator;
